import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Box, Modal, Typography, useTheme } from '@mui/material';
import { Button } from '@components/index';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { XIcon } from '@icons/index';
import { useResetUserMfa } from '@queryHooks/useMfa';
import type { TenantMemberSimpleModel } from '@repositories/tenantMemberRepository/models/TenantMemberSimpleModel';
import { getErrorMessage } from '@locales/i18nErrorMessage';

interface MfaResetModalProps {
  open: boolean;
  user: TenantMemberSimpleModel;
  onClose: () => void;
}

export const MfaResetModal = ({ open, user, onClose }: MfaResetModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    uiStore: { toastStore },
  } = useStore();
  const { mutate: resetUserMfa, isPending } = useResetUserMfa();

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    resetUserMfa(user.id, {
      onSuccess: () => {
        toastStore.open(t('Mfa.reset.success'));
        onClose();
      },
      onError: error => {
        toastStore.open(getErrorMessage(error).message);
      },
    });
  };
  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer>
        <DialogHeader>
          <HeaderTitle variant="headline6">{t('Mfa.reset.title')}</HeaderTitle>
          <CloseButton onClick={handleClose}>
            <XIcon width={24} height={24} color={theme.colors['ic-gray-main']} />
          </CloseButton>
        </DialogHeader>

        <DialogContent>
          <InfoSection>
            <InfoText>{t('Mfa.reset.description')}</InfoText>
          </InfoSection>

          <UserInfoSection>
            <UserInfoRow>
              <UserInfoLabel>{t('Mfa.reset.field.account')}</UserInfoLabel>
              <UserInfoValue>{user.username}</UserInfoValue>
            </UserInfoRow>

            <UserInfoRow>
              <UserInfoLabel>{t('Mfa.reset.field.name')}</UserInfoLabel>
              <UserInfoValue>{user.name}</UserInfoValue>
            </UserInfoRow>

            <UserInfoRow>
              <UserInfoLabel>{t('Mfa.reset.field.email')}</UserInfoLabel>
              <UserInfoValue>{user.email}</UserInfoValue>
            </UserInfoRow>

            <UserInfoRow>
              <UserInfoLabel>{t('Mfa.reset.field.status')}</UserInfoLabel>
              <StatusLabel isActive={user.mfaEnabled}>
                {user.mfaEnabled ? t('Mfa.reset.status.active') : t('Mfa.reset.status.inactive')}
              </StatusLabel>
            </UserInfoRow>
          </UserInfoSection>
        </DialogContent>

        <DialogFooter>
          <CustomButton variant="outline" size="extraLarge" onClick={handleClose} paddingHorizontal={24}>
            {t('GNB_Profile_MyProfile_11')}
          </CustomButton>
          <CustomSecureButton isPending={isPending} variant="contain" size="extraLarge" onClick={handleConfirm} paddingHorizontal={24}>
            {t('Mfa.reset.confirm')}
          </CustomSecureButton>
        </DialogFooter>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 480px;
  background-color: ${({ theme }) => theme.colors['bg-white']};
  border-radius: 6px;
  box-shadow: 0px 8px 10px -5px rgba(0, 0, 0, 0.08), 0px 16px 24px 2px rgba(0, 0, 0, 0.04),
    0px 6px 30px 5px rgba(0, 0, 0, 0.05);
  outline: none;
`;

const DialogHeader = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 18px 28px;
  border-bottom: none;
`;

const HeaderTitle = styled(Typography)`
  ${fonts.Headline6}
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const DialogContent = styled(Box)`
  padding: 31px 24px 0px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InfoSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoText = styled(Typography)`
  ${fonts.Body3}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  white-space: pre-line;
  line-height: 1.4285714285714286em;
  width: 400px;
` as typeof Typography;

const UserInfoSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 0px;
  border-top: 1px solid ${({ theme }) => theme.colors['border-gray-w-lighter']};
  border-bottom: 1px solid ${({ theme }) => theme.colors['border-gray-w-lighter']};
`;

const UserInfoRow = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
`;

const UserInfoLabel = styled(Typography)`
  ${fonts.Body3}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  width: 110px;
  flex-shrink: 0;
` as typeof Typography;

const UserInfoValue = styled(Typography)`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  flex: 1;
` as typeof Typography;

const StatusLabel = styled(Box)<{ isActive: boolean }>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: ${({ isActive }) => (isActive ? '0px 4px' : '1px 5px')};
  border-radius: 2px;
  ${fonts.Caption4}
  color: ${({ isActive, theme }) => (isActive ? theme.colors['text-white'] : theme.colors['text-gray-sub-darker'])};
  background-color: ${({ isActive, theme }) => (isActive ? theme.colors['bg-green'] : theme.colors['bg-gray-light'])};
  border: ${({ isActive, theme }) => (isActive ? 'none' : `0.5px solid ${theme.colors['border-gray-lighter']}`)};
`;

// const ReasonSection = styled(Box)`
//   display: flex;
//   flex-direction: column;
//   gap: 6px;
// `;

// const ReasonLabel = styled(Typography)`
//   ${fonts.Headline8}
//   color: ${({ theme }) => theme.colors['text-gray-main']};
// ` as typeof Typography;

const DialogFooter = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 24px 68px;
`;

const CustomButton = styled(Button)`
  width: 168px;
`;

const CustomSecureButton = styled(SecureSubmitButton)`
  width: 168px;
`;

// const StyledTextField = styled(TextField)`
//   & .MuiOutlinedInput-root {
//     padding: 10px 12px;
//     background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
//     ${fonts.Body2}

//     & fieldset {
//       border-color: ${({ theme }) => theme.colors['border-gray-light']};
//       border-radius: 2px;
//     }

//     &:hover fieldset {
//       border-color: ${({ theme }) => theme.colors['border-gray-light']};
//     }

//     &.Mui-focused {
//       background-color: ${({ theme }) => theme.colors['bg-white']};

//       & fieldset {
//         border-color: ${({ theme }) => theme.colors['border-primary']};
//         border-width: 1px;
//       }
//     }

//     &.Mui-error fieldset {
//       border-color: ${({ theme }) => theme.colors['border-red']};
//     }

//     & .MuiInputBase-input {
//       padding: 0;
//       ${fonts.Body2}
//       color: ${({ theme }) => theme.colors['text-gray-main']};

//       &::placeholder {
//         color: ${({ theme }) => theme.colors['text-gray-light']};
//         opacity: 1;
//       }
//     }
//   }
//   margin-bottom: 17px;
// `;
